import {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Box, Button, CircularProgress, Typography} from "@mui/material";
import {ArrowForward as BackIcon, ManageAccountsOutlined as ManagerIcon} from "@mui/icons-material";
import ManagerCapabilitiesPicker from "../componenets/managers/ManagerCapabilitiesPicker";
import ManagedTrainersPicker from "../componenets/managers/ManagedTrainersPicker";
import {getManager, updateManagerCapabilities, updateManagerTrainers} from "../services/ManagerService";
import {useMessageContext} from "../contexts/MessageContext";
import {RTL} from "../cache/CacheRtl";
import {CAPABILITIES} from "../enums/Capabilities";
import type {Capability} from "../enums/Capabilities";
import type {AdminManager} from "../api/generated";
import {colors, borders} from "../componenets/ui/colors";

/**
 * A manager's permissions, as a PAGE rather than a dialog.
 *
 * <p>It began as a dialog, when the only thing to decide was one checkbox and a trainer list. There
 * are now eleven permissions in two sections plus a trainer picker that can list every coach in the
 * company — more than a modal can show without its own scrollbar, and a modal that scrolls is where
 * an admin stops reading the descriptions and starts ticking boxes.
 *
 * <p>Routed at {@code /app/managers/:managerId/permissions}, which the navigation registry covers by
 * prefix: {@code resolveScreen} matches the longest registered path, so this inherits
 * {@code /app/managers} and its ADMIN-only rule with no second entry to keep in sync. That matters —
 * a permissions editor reachable by anyone but a company admin is the one screen that could let a
 * manager widen their own grant.
 */
const ManagerPermissionsPage = () => {
    const {managerId} = useParams();
    const navigate = useNavigate();
    const notify = useMessageContext()?.notificationHandler;

    const [manager, setManager] = useState<AdminManager | null>(null);
    const [loading, setLoading] = useState(true);
    const [capabilities, setCapabilities] = useState<Capability[]>([]);
    const [managesAll, setManagesAll] = useState<boolean>(true);
    const [trainerIds, setTrainerIds] = useState<number[]>([]);
    const [saving, setSaving] = useState(false);

    const backToList = () => navigate("/app/managers");

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        // By id, so a deep link or a refresh works — the page has to stand on its own now that it
        // is a route rather than a dialog opened from a row that was already in memory.
        getManager(Number(managerId))
            .then(({data}) => {
                if (cancelled) return;
                const found = data ?? null;
                setManager(found);
                setCapabilities((found?.capabilities || []) as Capability[]);
                setManagesAll(!!found?.managesAllTrainers);
                setTrainerIds((found?.managedTrainers || [])
                    .map(t => t.id)
                    .filter((id): id is number => typeof id === "number"));
            })
            .catch(() => {
                if (!cancelled) notify?.error("אירעה שגיאה בטעינת המנהל");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [managerId]);

    const handleSave = () => {
        if (!manager?.id) return;
        setSaving(true);
        updateManagerCapabilities(manager.id, capabilities)
            .then(() => updateManagerTrainers(manager.id as number, managesAll, managesAll ? [] : trainerIds))
            .then(() => notify?.success("ההרשאות עודכנו"))
            .then(backToList)
            .catch(() => notify?.error("אירעה שגיאה בעדכון ההרשאות"))
            .finally(() => setSaving(false));
    };

    // The trainer scope only means something to someone who manages trainers. Showing the picker
    // otherwise would ask the admin to choose whose clients a manager oversees, then store an answer
    // the server never consults — the manager reaches no trainee at all without this permission.
    const managesTrainers = capabilities.includes(CAPABILITIES.MANAGE_TRAINERS);

    if (loading) {
        return (
            <Box sx={{display: "flex", justifyContent: "center", padding: "48px"}}>
                <CircularProgress/>
            </Box>
        );
    }

    if (!manager) {
        return (
            <RTL>
                <Box sx={{padding: "24px"}}>
                    <Typography sx={{fontSize: "15px", marginBottom: "12px"}}>המנהל לא נמצא.</Typography>
                    <Button onClick={backToList}>חזרה לרשימת המנהלים</Button>
                </Box>
            </RTL>
        );
    }

    return (
        <RTL>
            <Box sx={{display: "flex", flexDirection: "column", gap: "18px"}}>
                <Box sx={{display: "flex", alignItems: "center", gap: "10px"}}>
                    <Button
                        onClick={backToList}
                        startIcon={<BackIcon/>}
                        sx={{color: colors.textSecondary, minWidth: 0}}
                    >
                        מנהלים
                    </Button>
                </Box>

                <Box sx={{
                    backgroundColor: colors.white, border: borders.default,
                    borderRadius: "12px", padding: "20px",
                }}>
                    <Box sx={{
                        display: "flex", alignItems: "center", gap: "8px",
                        paddingBottom: "12px", marginBottom: "18px", borderBottom: borders.default,
                    }}>
                        <ManagerIcon sx={{color: colors.textSecondary, fontSize: "20px"}}/>
                        <Typography sx={{fontSize: "17px", fontWeight: 600}}>
                            הרשאות — {manager.name}
                        </Typography>
                    </Box>

                    <ManagerCapabilitiesPicker value={capabilities} onChange={setCapabilities}/>

                    {managesTrainers && (
                        <Box sx={{marginTop: "24px", paddingTop: "18px", borderTop: borders.default}}>
                            <ManagedTrainersPicker
                                active={managesTrainers}
                                managesAll={managesAll}
                                onManagesAllChange={setManagesAll}
                                selectedTrainerIds={trainerIds}
                                onSelectedTrainerIdsChange={setTrainerIds}
                            />
                        </Box>
                    )}

                    <Box sx={{display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "24px"}}>
                        <Button onClick={backToList} disabled={saving}>ביטול</Button>
                        <Button variant="contained" onClick={handleSave} disabled={saving}>
                            שמור
                        </Button>
                    </Box>
                </Box>
            </Box>
        </RTL>
    );
};

export default ManagerPermissionsPage;
